import React, { FC, useState, useReducer, useEffect } from 'react';
import { Button, Divider } from '@mui/material';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Add } from '@mui/icons-material';
import {
  ImageItem,
  PromotionImageDetail,
  PromotionImageErrorMessage,
  PromotionImageFormMode,
  PromotionImageItem,
} from '../../../types/promotion-image-type';
import {
  BDM_PROMOTION_BASE_PATH,
  BDM_TEMPLETE_BASE_PATH,
  SALESKIT_BASE_PATH,
} from 'src/app/modules/sales-kit/constants';
import Form from 'src/app/common/components/Form';
import PromoteImageFormItem from './PromotionImageItem';
import moment from 'moment';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { create, update } from '../../../network/promotion-image-network';
import { cloneDeep, debounce } from 'lodash';
import { useStyles } from '../../../styles/promotion-image-style';

type PromotionImageDetailFormProps = {
  itemId?: string;
  formMode: PromotionImageFormMode;
  itemDetail?: any;
  onReload: () => void;
  onRouteTo: (route: string) => void;
};

export type PromotionImageItemType = {
  _id?: string | null;
  key: string;
  name: string;
  image: ImageItem | null;
  isDuplicateName?: boolean;
  errorNameMessage?: string | null;
};

export type PromotionImageFormState = {
  names: string[];
  namesFromServer: string[];
  forms: Array<PromotionImageItemType>;
};

type ReducerAction = {
  type: 'ADD_FORM' | 'DELETE_FORM' | 'UPDATE_NAME' | 'UPDATE_IMAGE' | 'UPDATE_ERROR_NAMES_FROM_SERVER';
  payload?: {
    index?: number;
    indexRemoveDuplicateNameError?: number;
    name?: string;
    image?: ImageItem | null;
    isDuplicateName?: boolean;
    errorNameMessage?: string | null;
    namesFromServer?: string[];
    indexList?: number[];
  };
};

const regexName =
  /^[a-zA-Z0-9ÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂẾưăạảấầẩẫậắằẳẵặẹẻẽềềểếỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵỷỹ_. ]*$/;
const initialState: PromotionImageFormState = {
  forms: [
    {
      key: moment(new Date()).format('HHmmssSSS'),
      name: '',
      image: null,
      isDuplicateName: false,
      errorNameMessage: null,
    },
  ],
  names: [''],
  namesFromServer: [],
};

const detailToStateConvertor = (item: PromotionImageItem): PromotionImageFormState => {
  return {
    forms: [
      {
        _id: item._id,
        name: item.name,
        image: item.image,
        key: moment(new Date()).format('HHmmssSSS'),
        isDuplicateName: false,
        errorNameMessage: null,
      },
    ],
    names: [item.name.toLowerCase().trim()],
    namesFromServer: [],
  };
};

const reducer = (state: PromotionImageFormState, action: ReducerAction): PromotionImageFormState => {
  switch (action.type) {
    case 'ADD_FORM':
      if (state.forms.length < 100) {
        state.forms.push({
          key: moment(new Date()).format('HHmmssSSS'),
          name: '',
          image: null,
          errorNameMessage: null,
        });
        state.names.push('');
        return { ...state };
      }
      return state;
    case 'DELETE_FORM':
      if (action.payload && typeof action.payload.index === 'number') {
        if (
          typeof action.payload.indexRemoveDuplicateNameError === 'number' &&
          action.payload.indexRemoveDuplicateNameError > -1 &&
          state.forms[action.payload.indexRemoveDuplicateNameError] &&
          state.forms[action.payload.indexRemoveDuplicateNameError].isDuplicateName
        ) {
          state.forms[action.payload.indexRemoveDuplicateNameError]['errorNameMessage'] = null;
          state.forms[action.payload.indexRemoveDuplicateNameError]['isDuplicateName'] = false;
        }

        state.forms.splice(action.payload.index, 1);
        state.names.splice(action.payload.index, 1);
        return { ...state };
      } else {
        return state;
      }
    case 'UPDATE_NAME':
      if (action.payload && typeof action.payload.index === 'number') {
        state.names[action.payload.index] = (action.payload.name || '').toLowerCase().trim();
        state.forms[action.payload.index]['name'] = action.payload.name || '';
        state.forms[action.payload.index]['errorNameMessage'] = action.payload.errorNameMessage || null;
        state.forms[action.payload.index]['isDuplicateName'] = action.payload.isDuplicateName || false;

        if (
          typeof action.payload.indexRemoveDuplicateNameError === 'number' &&
          action.payload.indexRemoveDuplicateNameError > -1 &&
          state.forms[action.payload.indexRemoveDuplicateNameError] &&
          state.forms[action.payload.indexRemoveDuplicateNameError].isDuplicateName
        ) {
          state.forms[action.payload.indexRemoveDuplicateNameError]['errorNameMessage'] = null;
          state.forms[action.payload.indexRemoveDuplicateNameError]['isDuplicateName'] = false;
        }
        return { ...state };
      } else {
        return state;
      }
    case 'UPDATE_IMAGE':
      if (action.payload && typeof action.payload.index === 'number') {
        state.forms[action.payload.index]['image'] = action.payload.image || null;
        return { ...state };
      } else {
        return state;
      }
    case 'UPDATE_ERROR_NAMES_FROM_SERVER':
      if (action.payload) {
        if (action.payload.indexList && action.payload.indexList.length > 0) {
          for (let index of action.payload.indexList) {
            if (state.forms[index]) {
              state.forms[index]['errorNameMessage'] = action.payload.errorNameMessage || null;
              state.forms[index]['isDuplicateName'] = action.payload.isDuplicateName || false;
            }
          }
        }

        state.namesFromServer = action.payload.namesFromServer || [];
        return { ...state };
      } else {
        return state;
      }
    default:
      return cloneDeep(initialState);
  }
};

const PromotionImageDetailForm: FC<PromotionImageDetailFormProps> = ({
  itemId,
  formMode,
  itemDetail,
  onReload,
  onRouteTo,
}) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const commonClasses = useCommonStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [isLoading, setIsLoading] = useState(false);
  const [formState, formDispatch] = useReducer(
    reducer,
    itemDetail ? detailToStateConvertor(itemDetail) : cloneDeep(initialState),
  );

  const handleAddForm = () => {
    formDispatch({ type: 'ADD_FORM' });
  };

  const handleDeleteForm = (index: number) => {
    let indexRemoveDuplicateNameError = findOneIndexUniqueNameOther(
      index,
      formState.forms[index].name.toLowerCase().trim(),
    );
    formDispatch({ type: 'DELETE_FORM', payload: { index, indexRemoveDuplicateNameError } });
  };

  const handleChange = (index: number, fieldName: string, value: string | ImageItem) => {
    if (fieldName === 'name' && typeof value === 'string') {
      let errorNameMessage = null;
      let isDuplicateName = false;

      if (value.toLowerCase().trim() !== '') {
        if (!regexName.test(value)) {
          errorNameMessage = PromotionImageErrorMessage.SPECIAL_CHARACTERS;
        } else {
          let indexNames = formState.names.indexOf(value.toLowerCase().trim());
          isDuplicateName =
            (indexNames !== -1 && indexNames !== index) ||
            formState.namesFromServer.indexOf(value.toLowerCase().trim()) !== -1
              ? true
              : false;
          errorNameMessage = isDuplicateName ? PromotionImageErrorMessage.DUPLICATE_NAME : null;
        }
      } else {
        isDuplicateName = false;
        errorNameMessage = null;
      }

      formDispatch({
        type: 'UPDATE_NAME',
        payload: {
          index,
          name: value,
          errorNameMessage,
          isDuplicateName,
          indexRemoveDuplicateNameError: findOneIndexUniqueNameOther(
            index,
            formState.forms[index].name.toLowerCase().trim(),
          ),
        },
      });
    } else if (fieldName === 'image' && typeof value === 'object') {
      formDispatch({
        type: 'UPDATE_IMAGE',
        payload: {
          index,
          image: value,
        },
      });
    }
  };

  const findOneIndexUniqueNameOther = (index: number, valueOld: string) => {
    if (formState.forms.length < 2 || valueOld === '') {
      return -1;
    }

    let indexFound = -1;
    formState.names[index] = '';
    let indexLoop = formState.names.indexOf(valueOld);

    if (indexLoop !== -1) {
      let totalDuplicate = 0;
      for (let i = indexLoop; i < formState.names.length; i++) {
        if (formState.names[i] === valueOld) {
          totalDuplicate++;
          if (totalDuplicate > 1) {
            return -1;
          }
          indexFound = i;
        }
      }
    }

    return indexFound;
  };

  const onSubmitForm = debounce(async () => {
    setIsLoading(true);
    let index = 0;
    const apiData: PromotionImageDetail[] = [];

    try {
      for (index; index < formState.forms.length; index++) {
        if (formState.forms[index].errorNameMessage) {
          setIsLoading(false);
          return false;
        }

        if (formState.forms[index].name.trim() !== '' && formState.forms[index].image) {
          apiData.push({
            name: formState.forms[index].name.trim(),
            image: formState.forms[index].image as ImageItem,
          });
        } else {
          setIsLoading(false);
          return false;
        }
      }

      if (apiData.length === 0) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.WARNING,
              title: Translation('global.submit.fail'),
              content: PromotionImageErrorMessage.SOMETHING_WRONG,
            },
          ]),
        );
        setIsLoading(false);
        return false;
      }

      if (formMode === PromotionImageFormMode.CREATE) {
        const createResponse = await create(apiData, dispatch);
        if (createResponse.tag === 'FAIL') {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.WARNING,
                title: Translation('global.submit.fail'),
                content: createResponse.message || 'System Error',
              },
            ]),
          );

          if (createResponse?.errorCode === 'E005' && createResponse?.duplicateKeys?.length > 0) {
            let duplicateKeys = createResponse?.duplicateKeys.map((item) => item.toLowerCase().trim());
            let indexList: number[] = [];
            index = 0;
            for (index; index < formState.names.length; index++) {
              if (duplicateKeys.indexOf(formState.names[index]) !== -1) {
                indexList.push(index);
              }
            }

            formDispatch({
              type: 'UPDATE_ERROR_NAMES_FROM_SERVER',
              payload: {
                indexList,
                errorNameMessage: PromotionImageErrorMessage.DUPLICATE_NAME,
                isDuplicateName: true,
                namesFromServer: createResponse?.duplicateKeys,
              },
            });
          }
        } else {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: `Created successfully - ${formState.forms.length} promotion images`,
              },
            ]),
          );
          onRouteTo(`${SALESKIT_BASE_PATH}${BDM_TEMPLETE_BASE_PATH}${BDM_PROMOTION_BASE_PATH}`);
        }
      } else {
        apiData[0]._id = itemId;

        const updateResponse = await update(apiData[0], dispatch);
        if (updateResponse?.tag === 'FAIL') {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.WARNING,
                title: Translation('global.submit.fail'),
                content: updateResponse.message || 'System Error',
              },
            ]),
          );

          if (updateResponse?.errorCode === 'E005') {
            formDispatch({
              type: 'UPDATE_ERROR_NAMES_FROM_SERVER',
              payload: {
                indexList: [0],
                errorNameMessage: PromotionImageErrorMessage.DUPLICATE_NAME,
                isDuplicateName: true,
                namesFromServer: [formState.forms[0].name.toLowerCase().trim()],
              },
            });
          }
        } else {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: `Updated successfully`,
              },
            ]),
          );
          onRouteTo(`${SALESKIT_BASE_PATH}${BDM_TEMPLETE_BASE_PATH}${BDM_PROMOTION_BASE_PATH}`);
        }
      }
    } catch (err) {
      console.log('onSubmitForm error', err);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.WARNING,
            title: Translation('global.submit.fail'),
            content: PromotionImageErrorMessage.SOMETHING_WRONG,
          },
        ]),
      );
    }

    setTimeout(() => setIsLoading(false), 2000);
  }, 500);

  return (
    <>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.classes.header}>
              {formMode === PromotionImageFormMode.CREATE ? 'Add New Promotion Image' : 'Edit Promotion Image'}
            </div>
          </div>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => onRouteTo(`${SALESKIT_BASE_PATH}${BDM_TEMPLETE_BASE_PATH}${BDM_PROMOTION_BASE_PATH}/list`)}
          >
            Back
          </Button>
        </div>
        <div className={classes.container}>
          <Form autoComplete="off" onFinish={onSubmitForm} initialValues={initialState}>
            {formState.forms.map((item, index) => (
              <div key={item.key}>
                <PromoteImageFormItem index={index} onChange={handleChange} itemInput={item} />
                {formState.forms.length > 1 && (
                  <div className={classes.sectionMargin}>
                    <div className={classes.rowContainer}>
                      <div className={classes.fieldContainer}></div>
                      <div style={{ flexGrow: 1 }}>
                        <span className={classes.deleteButton} onClick={() => handleDeleteForm(index)}>
                          Delete record
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div className={classes.sectionMargin}>
                  <Divider />
                </div>
              </div>
            ))}
            {formMode === PromotionImageFormMode.CREATE && (
              <div className={classes.sectionMargin}>
                <div className={classes.rowContainer}>
                  <div className={classes.fieldContainer}></div>
                  <div style={{ flexGrow: 1 }}>
                    <Button
                      type="button"
                      variant="contained"
                      color="secondary"
                      onClick={handleAddForm}
                      disabled={formState.forms.length >= 100}
                    >
                      <Add />
                      Add New Record
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className={classes.footerContainer}>
              <Button
                variant="contained"
                color="inherit"
                onClick={() =>
                  onRouteTo(`${SALESKIT_BASE_PATH}${BDM_TEMPLETE_BASE_PATH}${BDM_PROMOTION_BASE_PATH}/list`)
                }
              >
                Cancer
              </Button>
              <Button
                style={{ marginLeft: 25 }}
                variant="contained"
                disabled={isLoading}
                type="submit"
                color="secondary"
              >
                {formMode === PromotionImageFormMode.CREATE
                  ? Translation('app.button.submit')
                  : Translation('app.button.save')}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default PromotionImageDetailForm;
